import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, useMotionValue } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Code, MessageSquare, Zap, Shield, Users, Database, Target, Layers, Menu, X } from 'lucide-react';
import lightLogo from '../../assets/image001.png';

// Responsive Header
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/">
              <img 
                src={lightLogo} 
                alt="MenaDevs AI Logo" 
                className="h-10 w-auto"
              />
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-10">
            {[
              { name: 'About', href: '#about' },
              { name: 'Services', href: '#services' },
              { name: 'Metrics', href: '#metrics' },
              // { name: 'Features', href: '#features' }
            ].map((item) => (
              <a 
                key={item.name} 
                href={item.href}
                className="text-gray-700 hover:text-[#3de533] transition-colors font-medium"
              >
                {item.name}
              </a>
            ))}
          </nav>
          
          {/* CTA Button - Desktop */}
          <div className="hidden md:block">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link 
                to="/login" 
                className="inline-block px-5 py-2 bg-gradient-to-r from-[#3de533] to-[#2dcc29] text-white font-medium rounded-lg"
              >
                Login
              </Link>
            </motion.div>
          </div>
          
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Menu */}
      <motion.div 
        initial={{ height: 0, opacity: 0 }}
        animate={{ 
          height: isOpen ? 'auto' : 0,
          opacity: isOpen ? 1 : 0
        }}
        className="md:hidden overflow-hidden bg-white"
      >
        <div className="px-4 pt-2 pb-6 space-y-4">
          {[
            { name: 'About', href: '#about' },
            { name: 'Services', href: '#services' },
            { name: 'Metrics', href: '#metrics' },
            { name: 'Features', href: '#features' }
          ].map((item) => (
            <a 
              key={item.name} 
              href={item.href}
              className="block py-2 text-gray-700 hover:text-[#3de533] transition-colors font-medium"
              onClick={() => setIsOpen(false)}
            >
              {item.name}
            </a>
          ))}
          <motion.div
            whileTap={{ scale: 0.95 }}
            className="w-full mt-4 px-5 py-3 bg-gradient-to-r from-[#3de533] to-[#2dcc29] text-white font-medium rounded-lg"
          >
            <Link 
              to="/login" 
              className="block w-full text-center"
            >
              Login
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </header>
  );
};

// Neural Mesh Background
const NeuralMesh = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      <svg className="absolute inset-0 w-full h-full" style={{ filter: 'blur(0.5px)' }}>
        <defs>
          <radialGradient id="nodeGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#3de533" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#3de533" stopOpacity="0" />
          </radialGradient>
        </defs>
        {[...Array(12)].map((_, i) => {
          const angle = (i / 12) * Math.PI * 2;
          const radius = 200 + Math.sin(Date.now() * 0.001 + i) * 50;
          const x = window.innerWidth / 2 + Math.cos(angle) * radius;
          const y = window.innerHeight / 2 + Math.sin(angle) * radius;
          
          return (
            <g key={i}>
              <circle
                cx={x + (mousePos.x - window.innerWidth / 2) * 0.02}
                cy={y + (mousePos.y - window.innerHeight / 2) * 0.02}
                r="3"
                fill="url(#nodeGradient)"
              />
              {[...Array(3)].map((_, j) => {
                const nextIndex = (i + j + 1) % 12;
                const nextAngle = (nextIndex / 12) * Math.PI * 2;
                const nextRadius = 200 + Math.sin(Date.now() * 0.001 + nextIndex) * 50;
                const nextX = window.innerWidth / 2 + Math.cos(nextAngle) * nextRadius;
                const nextY = window.innerHeight / 2 + Math.sin(nextAngle) * nextRadius;
                
                return (
                  <line
                    key={j}
                    x1={x + (mousePos.x - window.innerWidth / 2) * 0.02}
                    y1={y + (mousePos.y - window.innerHeight / 2) * 0.02}
                    x2={nextX + (mousePos.x - window.innerWidth / 2) * 0.02}
                    y2={nextY + (mousePos.y - window.innerHeight / 2) * 0.02}
                    stroke="#3de533"
                    strokeWidth="0.5"
                    strokeOpacity="0.3"
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Revolutionary Terminal Interface
const AITerminal = () => {
  const [commands, setCommands] = useState([]);
  const [currentCommand, setCurrentCommand] = useState('');
  
  useEffect(() => {
    const terminalCommands = [
      { cmd: '$ initialize fine-tuning --model=base-LLM', output: '✓ Fine-tuning process initialized' },
      { cmd: '$ optimize model --method=RLHF', output: '✓ Human feedback incorporated: 347% performance gain' },
      { cmd: '$ train alignment --region=MENA', output: '✓ Model alignment improved by 89%' },
      { cmd: '$ deploy optimized-model --client=Enterprise', output: '✓ Model deployed with 4.2x efficiency' }
    ];
    
    let index = 0;
    const interval = setInterval(() => {
      if (index < terminalCommands.length) {
        setCommands(prev => [...prev, terminalCommands[index]]);
        index++;
      } else {
        clearInterval(interval);
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-black/90 backdrop-blur-xl rounded-lg p-4 font-mono text-sm border border-[#3de533]/30 shadow-2xl">
      <div className="flex items-center mb-3">
        <div className="flex space-x-2">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
        </div>
        <span className="ml-4 text-gray-400">MENADEVS AI Terminal</span>
      </div>
      <div className="space-y-2 max-h-40 overflow-hidden">
        {Array.isArray(commands) && commands.map((cmd, i) => (
          <div key={i}>
            <div className="text-[#3de533]">{cmd?.cmd || ''}</div>
            <div className="text-gray-300 ml-4">{cmd?.output || ''}</div>
          </div>
        ))}
        <div className="text-[#3de533] flex items-center">
          $ <span className="ml-2 animate-pulse">|</span>
        </div>
      </div>
    </div>
  );
};

// Morphing Logo
const MorphingLogo = () => {
  return (
    <div className="relative w-32 h-32 mx-auto mb-8">
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{
          background: [
            "linear-gradient(45deg, #3de533, #2dcc29)",
            "linear-gradient(135deg, #2dcc29, #1db914)",
            "linear-gradient(225deg, #1db914, #3de533)",
            "linear-gradient(315deg, #3de533, #2dcc29)"
          ],
          rotate: [0, 90, 180, 270, 360],
          scale: [1, 1.1, 1, 0.9, 1]
        }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute inset-2 rounded-full bg-white flex items-center justify-center"
        animate={{ rotate: [360, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      >
        <Brain className="h-12 w-12 text-[#3de533]" />
      </motion.div>
    </div>
  );
};

// Liquid Button
const LiquidButton = ({ children, onClick, className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.button
      className={`relative overflow-hidden rounded-full px-8 py-4 font-bold text-white ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      whileTap={{ scale: 0.95 }}
    >
      <motion.div
        className="absolute inset-0 bg-gradient-to-r from-[#3de533] to-[#2dcc29]"
        animate={isHovered ? {
          background: [
            "linear-gradient(0deg, #3de533, #2dcc29)",
            "linear-gradient(90deg, #2dcc29, #1db914)",
            "linear-gradient(180deg, #1db914, #3de533)",
            "linear-gradient(270deg, #3de533, #2dcc29)"
          ]
        } : {}}
        transition={{ duration: 0.8, repeat: isHovered ? Infinity : 0 }}
      />
      <motion.div
        className="absolute inset-0"
        animate={isHovered ? {
          background: "radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(255,255,255,0.2) 0%, transparent 50%)"
        } : {}}
      />
      <span className="relative z-10 flex items-center">{children}</span>
    </motion.button>
  );
};

// Data Visualization Cards
const DataCard = ({ title, value, trend, icon: Icon }) => {
  return (
    <motion.div
      whileHover={{ y: -10, rotateY: 5 }}
      className="relative p-6 bg-gradient-to-br from-white/80 to-white/40 backdrop-blur-xl rounded-2xl border border-white/30 shadow-xl"
      style={{ 
        transformStyle: 'preserve-3d',
        perspective: '1000px'
      }}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon className="h-8 w-8 text-[#3de533]" />
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 rounded-full bg-gradient-to-r from-[#3de533]/20 to-transparent"
        />
      </div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">{title}</h3>
      <div className="text-3xl font-black text-gray-900 mb-1">{value}</div>
      <div className="text-sm text-[#3de533] font-medium">{trend}</div>
    </motion.div>
  );
};

const LandingPage = () => {
  const containerRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.8]);
  const heroRotate = useTransform(scrollYProgress, [0, 1], [0, -5]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <div ref={containerRef} className="relative bg-gradient-to-br from-slate-50 via-white to-gray-100 overflow-hidden">
      <Header />
      <div className="pt-16"> {/* Reduced padding top to account for fixed header */}
        {/* Revolutionary Hero */}
        <motion.section
          id="about"
          style={{ scale: heroScale, rotateX: heroRotate }}
          className="relative min-h-screen flex items-start pt-8 md:pt-16 justify-center"
        >
          <NeuralMesh />
          
          {/* Floating Elements */}
          <motion.div
            style={{ x: mouseX, y: mouseY }}
            className="absolute pointer-events-none"
            transition={{ type: "spring", damping: 30 }}
          >
            <div className="w-4 h-4 rounded-full bg-[#3de533]/30 blur-sm" />
          </motion.div>

          <div className="relative z-10 max-w-7xl mx-auto px-6">
            
            {/* Modern Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl">
                <div className="w-2 h-2 rounded-full bg-[#3de533] animate-pulse mr-3" />
                <span className="text-gray-700 font-medium text-sm">AI Revolution • MENA Region</span>
              </div>
            </motion.div>

            {/* Clean Modern Heading */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold leading-none mb-8">
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                  className="inline text-gray-900"
                >
                  MENA
                </motion.span>
                <motion.span 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="inline bg-gradient-to-r from-[#3de533] via-[#2dcc29] to-[#1db914] bg-clip-text text-transparent"
                >
                  DEVS
                </motion.span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                  className="inline text-gray-900"
                >
                   AI
                </motion.span>
              </h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
              >
                Elevating AI performance through 
                <span className="text-[#3de533] font-semibold"> specialized fine-tuning</span> and 
                <span className="text-[#3de533] font-semibold"> reinforcement learning from human feedback</span>
              </motion.p>
            </motion.div>

            {/* Modern Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              
              {/* Left Side - CTA */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 }}
                className="space-y-8"
              >
                <div className="space-y-6">
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                    Ready to enhance your AI models?
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    Unlock the full potential of your models with expert fine-tuning and RLHF implementation. Experience AI that truly aligns with human values and business needs.
                  </p>
                </div>

                {/* <div className="flex flex-col sm:flex-row gap-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#3de533] to-[#2dcc29] text-white font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
                  >
                    <span className="flex items-center">
                      Schedule Consultation
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center justify-center px-8 py-4 bg-white/80 backdrop-blur-xl border-2 border-gray-200 text-gray-700 font-bold rounded-2xl hover:border-[#3de533]/50 hover:text-[#3de533] transition-all duration-300 shadow-lg"
                  >
                    Our Methodology
                  </motion.button>
                </div> */}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 pt-8">
                  {[
                    { number: "120+", label: "Models Optimized" },
                    { number: "85%", label: "Efficiency Gain" },
                    { number: "3.4x", label: "ROI Increase" }
                  ].map((stat, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.2 + i * 0.1 }}
                      className="text-center"
                    >
                      <div className="text-2xl md:text-3xl font-black text-gray-900">{stat.number}</div>
                      <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Right Side - Terminal */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 }}
                className="relative"
              >
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-[#3de533]/20 to-[#2dcc29]/20 rounded-3xl blur-xl" />
                
                {/* Terminal Container */}
                <div className="relative">
                  <AITerminal />
                </div>
                
                {/* Floating Elements */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-r from-[#3de533] to-[#2dcc29] rounded-full flex items-center justify-center shadow-xl"
                >
                  <Brain className="h-6 w-6 text-white" />
                </motion.div>
                
                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                  className="absolute -bottom-6 -left-6 w-10 h-10 bg-gradient-to-r from-[#2dcc29] to-[#1db914] rounded-full flex items-center justify-center shadow-xl"
                >
                  <Zap className="h-5 w-5 text-white" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Prompt Mastery Section */}
        <section id="metrics" className="relative py-24 bg-gradient-to-b from-white via-gray-50 to-white">
          <div className="absolute inset-0 bg-circuit-pattern opacity-5" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-20"
            >
              <h2 className="text-5xl font-black text-gray-900 mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3de533] to-[#2dcc29]">
                  PROMPT MASTERY
                </span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Advanced prompt engineering techniques that unlock the full potential of AI systems
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              {[
                {
                  icon: Brain,
                  title: "Advanced Prompt Architecture",
                  description: "Complex multi-layered prompts designed for maximum AI comprehension and output quality across all major language models",
                  color: "from-[#3de533] to-[#2dcc29]"
                },
                {
                  icon: Target,
                  title: "Context Optimization",
                  description: "Precision-crafted context windows that maximize token efficiency while maintaining conversation coherence and accuracy",
                  color: "from-[#3de533] to-[#2dcc29]"
                },
                {
                  icon: Layers,
                  title: "Chain-of-Thought Engineering",
                  description: "Advanced reasoning frameworks that guide AI through complex problem-solving processes with step-by-step logical progression",
                  color: "from-[#3de533] to-[#2dcc29]"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="group relative h-[400px]"
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-[#3de533] to-[#2dcc29] rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative h-full p-8 bg-white rounded-lg border border-[#3de533]/20 shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#3de533]/10 text-[#3de533] mb-4">
                      <feature.icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-[#3de533] transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors flex-grow">
                      {feature.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Revolutionary Features */}
        <section id="services" className="relative py-24 bg-gradient-to-b from-white via-green-50/30 to-white overflow-hidden">
          <motion.div
            style={{ y: useTransform(scrollYProgress, [0, 1], ["0%", "50%"]) }}
            className="absolute inset-0 bg-hologram-pattern opacity-5"
          />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className="text-center mb-20"
            >
              <h2 className="text-5xl font-black text-gray-900 mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3de533] via-[#2dcc29] to-[#1db914]">
                  OUR SERVICES
                </span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: MessageSquare,
                  title: "Custom Prompt Development",
                  description: "Bespoke prompt engineering solutions tailored to your specific AI applications and business requirements",
                  gradient: "from-[#3de533]/20 to-[#2dcc29]/20"
                },
                {
                  icon: Code,
                  title: "AI Model Fine-Tuning",
                  description: "Expert optimization of language models through advanced prompt strategies and parameter adjustments",
                  gradient: "from-[#3de533]/20 to-[#2dcc29]/20"
                },
                {
                  icon: Users,
                  title: "Team Training & Workshops",
                  description: "Comprehensive prompt engineering training programs for developers and AI practitioners in the MENA region",
                  gradient: "from-[#3de533]/20 to-[#2dcc29]/20"
                },
                {
                  icon: Shield,
                  title: "AI Safety & Alignment",
                  description: "Robust prompt safety measures to ensure AI outputs align with ethical guidelines and business standards",
                  gradient: "from-[#3de533]/20 to-[#2dcc29]/20"
                },
                {
                  icon: Database,
                  title: "Prompt Library Solutions",
                  description: "Curated collections of high-performance prompts for various industries and use cases",
                  gradient: "from-[#3de533]/20 to-[#2dcc29]/20"
                },
                {
                  icon: Zap,
                  title: "Performance Optimization",
                  description: "Advanced techniques to maximize AI response quality, speed, and cost-effectiveness",
                  gradient: "from-[#3de533]/20 to-[#2dcc29]/20"
                }
              ].map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="bg-white/80 backdrop-blur-xl rounded-xl p-8 border border-[#3de533]/20 hover:border-[#3de533]/50 transition-all duration-300 shadow-lg hover:shadow-xl group"
                >
                  <div className="text-[#3de533] mb-4"><service.icon className="h-8 w-8" /></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#3de533] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Experimental CTA */}
        {/* <section id="features" className="relative py-24 bg-gray-50">
          <div className="absolute inset-0 bg-data-stream opacity-10" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#3de533]/10 to-[#2dcc29]/10 border border-[#3de533]/30 shadow-2xl"
            >
              <div className="absolute inset-0 bg-holographic-noise opacity-10" />
              <div className="relative px-12 py-20 text-center">
                <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3de533] via-[#2dcc29] to-[#1db914]">
                    READY TO OPTIMIZE?
                  </span>
                </h2>
                <p className="text-xl text-gray-700 mb-10 max-w-3xl mx-auto leading-relaxed">
                  Transform your AI interactions with expertly crafted prompts. 
                  Let MENADEVS unlock the full potential of your AI systems.
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link 
                    to="/login" 
                    className="inline-flex items-center px-8 py-4 text-lg font-bold text-white bg-gradient-to-r from-[#3de533] to-[#2dcc29] rounded-full hover:from-[#2dcc29] hover:to-[#3de533] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
                  >
                    Transform Your Models
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </section> */}

        {/* Footer */}
        <footer className="bg-black text-white py-16">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.div
              animate={{
                textShadow: [
                  "0 0 20px #3de533",
                  "0 0 40px #3de533",
                  "0 0 20px #3de533"
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-4xl font-black mb-8"
            >
              Mena<span className="text-[#3de533]">Devs</span> AI
            </motion.div>
            <div className="text-gray-400">
              © 2025 • Neural Revolution Begins
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;